import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import * as pdfjs from "pdfjs-dist";
import { Col, Row } from "reactstrap";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";
import { buildSavedPdfBytes } from "helpers/pdfTextReplace";
import { pdfBytesToFile, saveSinglePagePdfBytes } from "helpers/pdfLibEditor";
import { extractTextItemsFromPdfBytes } from "helpers/pdfPureText";
import {
    buildEditableTextBlocks,
    buildEditableBlocksFromWidgetAnnotations,
    dedupeOverlappingExtractedBlocks,
    mergeExtractedAndFormBlocks,
    blankCustomBlockFromErasureZone,
    blockIsHiddenByErasureZone,
    buildBlankSlotsFromErasureReplacements,
    erasureZoneFromEditEntry,
    expandEraseEditEntry,
    hiddenPdfBlockIdsForErase,
    isErasedEdit,
    mergeTextBlocksForEditor,
    resolveBlockDisplayText,
} from "helpers/pdfTextLayer";
import {
    cssFontStyleForKey,
    cssFontWeightForKey,
    fontMetaFromPdfKey,
    NEW_TEXT_FONT_OPTIONS,
} from "helpers/pdfFontMapping";
import {
    colorToCss,
    hexToNormalizedRgb,
    rgbToHex,
    sampleTextAndFillColors,
} from "helpers/pdfTextColorSample";

const PDFJS_CDN_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}`;

if (typeof window !== "undefined") {
    pdfjs.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN_BASE}/build/pdf.worker.min.mjs`;
}

const buildPdfJsDocumentInit = (data) => ({
    data,
    enableXfa: true,
    useSystemFonts: true,
    cMapUrl: `${PDFJS_CDN_BASE}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${PDFJS_CDN_BASE}/standard_fonts/`,
});

const ZOOM_LEVELS = [0.5, 0.75, 0.9, 1, 1.15, 1.35, 1.6, 1.85];
const DEFAULT_ZOOM_INDEX = 0;
const THUMB_MAX_WIDTH = 120;
const LINE_HEIGHT_FACTOR = 1.2;
const DEFAULT_INSERT_FONT_SIZE = 30;
const MIN_CUSTOM_FONT_SIZE = 6;
const MAX_CUSTOM_FONT_SIZE = 72;

const resolveTextColor = (block, edit) => {
    if (edit && Number.isFinite(edit.textColorR)) {
        return {
            r: edit.textColorR,
            g: edit.textColorG,
            b: edit.textColorB,
        };
    }
    return block.textColor || { r: 0, g: 0, b: 0 };
};

const rgbChannelsEqual = (a, b) => {
    const left = a || { r: 0, g: 0, b: 0 };
    const right = b || { r: 0, g: 0, b: 0 };
    return (
        Math.abs((left.r ?? 0) - (right.r ?? 0)) < 0.002 &&
        Math.abs((left.g ?? 0) - (right.g ?? 0)) < 0.002 &&
        Math.abs((left.b ?? 0) - (right.b ?? 0)) < 0.002
    );
};

const stylePatchFromEdit = (block, edit) => {
    if (!edit) return null;
    const pdfFontKey = edit.pdfFontKey ?? block.pdfFontKey ?? "Helvetica";
    return {
        fontSize: edit.fontSize ?? block.fontSize,
        textColor: resolveTextColor(block, edit),
        ...fontMetaFromPdfKey(pdfFontKey),
    };
};

const editStyleFieldsFromPatch = (block, stylePatch) => {
    const fontSize = stylePatch?.fontSize ?? block.fontSize;
    const textColor = stylePatch?.textColor ?? block.textColor ?? { r: 0, g: 0, b: 0 };
    const originalColor = block.textColor || { r: 0, g: 0, b: 0 };
    const pdfFontKey = stylePatch?.pdfFontKey ?? block.pdfFontKey ?? "Helvetica";
    const fontMeta =
        stylePatch?.cssFontFamily != null
            ? {
                  pdfFontKey,
                  cssFontFamily: stylePatch.cssFontFamily,
                  cssFontWeight: stylePatch.cssFontWeight ?? block.cssFontWeight,
                  cssFontStyle: stylePatch.cssFontStyle ?? block.cssFontStyle,
              }
            : fontMetaFromPdfKey(pdfFontKey);
    const styleChanged =
        Number(fontSize) !== Number(block.fontSize) ||
        !rgbChannelsEqual(textColor, originalColor) ||
        String(pdfFontKey) !== String(block.pdfFontKey ?? "Helvetica");
    return {
        fontSize,
        textColorR: textColor.r ?? 0,
        textColorG: textColor.g ?? 0,
        textColorB: textColor.b ?? 0,
        styleChanged,
        ...fontMeta,
    };
};

const layoutBlockOnViewport = (block, viewport, displayText = " ") => {
    if (!viewport) return block;
    return {
        ...block,
        ...toViewportBox(
            {
                originalText: displayText || " ",
                fontSize: block.fontSize,
                pdfX: block.pdfX,
                pdfY: block.pdfY,
                pdfWidth: block.pdfWidth,
                pdfHeight: block.pdfHeight,
            },
            viewport
        ),
    };
};

const repositionCustomBlockViewport = (
    block,
    viewportLeft,
    viewportTop,
    viewport,
    zoomScale,
    displayText
) => {
    if (!viewport) return block;
    const fontSize = Number(block.fontSize) || DEFAULT_INSERT_FONT_SIZE;
    const fontHeightViewport = fontSize * zoomScale;
    const [pdfX, pdfY] = viewport.convertToPdfPoint(
        viewportLeft,
        viewportTop + fontHeightViewport
    );
    const laidOut = layoutBlockOnViewport(
        { ...block, pdfX, pdfY },
        viewport,
        displayText
    );
    return {
        ...laidOut,
        placementViewportX: viewportLeft,
        placementViewportY: viewportTop,
    };
};

const pinCustomBlockPlacement = (block, laidOut) => {
    const anchorLeft = block.placementViewportX ?? block.left ?? laidOut.left;
    const anchorTop = block.placementViewportY ?? block.top ?? laidOut.top;
    return {
        ...laidOut,
        left: anchorLeft,
        top: anchorTop,
        placementViewportX: anchorLeft,
        placementViewportY: anchorTop,
    };
};

const defaultFontSizeForBlocks = (blocks = []) => {
    const sizes = blocks
        .map((block) => Number(block?.fontSize))
        .filter((size) => Number.isFinite(size) && size > 0);
    if (!sizes.length) return DEFAULT_INSERT_FONT_SIZE;
    const total = sizes.reduce((sum, size) => sum + size, 0);
    return Math.round((total / sizes.length) * 10) / 10;
};

const toViewportBox = (block, viewport) => {
    const fakeItem = {
        str: block.originalText,
        width: block.pdfWidth,
        height: block.pdfHeight,
        transform: [block.fontSize, 0, 0, block.fontSize, block.pdfX, block.pdfY],
    };
    const transform = pdfjs.Util.transform(viewport.transform, fakeItem.transform);
    const fontHeight = Math.hypot(transform[2], transform[3]) || block.fontSize;
    const left = transform[4];
    const top = transform[5] - fontHeight;
    const width = Math.max(
        block.pdfWidth * viewport.scale,
        block.originalText.length * fontHeight * 0.45
    );

    return {
        left,
        top,
        width,
        height: Math.max(fontHeight * 1.2, 18),
        viewportFontSize: fontHeight,
    };
};

const blockTypography = (block, edit) => ({
    fontSize: edit?.fontSize ?? block.fontSize,
    pdfFontKey: edit?.pdfFontKey ?? block.pdfFontKey ?? "Helvetica",
    cssFontFamily: edit?.cssFontFamily ?? block.cssFontFamily,
    cssFontWeight: edit?.cssFontWeight ?? block.cssFontWeight ?? "400",
    cssFontStyle: edit?.cssFontStyle ?? block.cssFontStyle ?? "normal",
});

const styleEditShapeFromBlock = (block) => {
    if (!block) return null;
    return {
        fontSize: block.fontSize,
        textColorR: block.textColor?.r ?? 0,
        textColorG: block.textColor?.g ?? 0,
        textColorB: block.textColor?.b ?? 0,
        pdfFontKey: block.pdfFontKey ?? "Helvetica",
        cssFontFamily: block.cssFontFamily,
        cssFontWeight: block.cssFontWeight ?? "400",
        cssFontStyle: block.cssFontStyle ?? "normal",
    };
};

const PDF_TEXT_WHITOUT_PADDING = 10;
const PDF_EDIT_SURFACE = "#ffffff";

const PDF_EDITOR_TOOLBAR_FOCUS_SELECTOR =
    ".pdf-inline-text-editor-style-panel, .pdf-pit-toolbar-left, .pdf-pit-toolbar-tools, .pdf-pit-toolbar-style-tools";

const isToolbarFocusTarget = (node) =>
    Boolean(
        node &&
            typeof node.closest === "function" &&
            node.closest(PDF_EDITOR_TOOLBAR_FOCUS_SELECTOR)
    );

const canvasFontString = (typography, viewportFontSize) => {
    const weight = typography.cssFontWeight || cssFontWeightForKey(typography.pdfFontKey);
    const style = typography.cssFontStyle || cssFontStyleForKey(typography.pdfFontKey);
    const family = typography.cssFontFamily || "Helvetica, Arial, sans-serif";
    return `${style} ${weight} ${viewportFontSize}px ${family}`;
};

let sharedMeasureCanvasCtx = null;

const measureViewportBoxForContent = (block, text, typography, zoomScale) => {
    const viewportFontSize = Math.max(
        8,
        Number(typography.fontSize) * zoomScale
    );
    const lines = String(text ?? "").split(/\r?\n/);
    const lineCount = Math.max(1, lines.length);
    if (!sharedMeasureCanvasCtx) {
        const canvas = document.createElement("canvas");
        sharedMeasureCanvasCtx = canvas.getContext("2d");
    }
    sharedMeasureCanvasCtx.font = canvasFontString(typography, viewportFontSize);
    let longestWidth = 0;
    lines.forEach((line) => {
        longestWidth = Math.max(
            longestWidth,
            sharedMeasureCanvasCtx.measureText(String(line).length ? line : " ").width
        );
    });

    return {
        left: block.left,
        top: block.top,
        width: Math.max(block.width, longestWidth + 2, 12),
        height: Math.max(
            block.height,
            lineCount * viewportFontSize * LINE_HEIGHT_FACTOR,
            viewportFontSize * LINE_HEIGHT_FACTOR
        ),
        fontSize: viewportFontSize,
    };
};

const maskRectCoveringText = (block, typography, originalText, displayText, zoomScale) => {
    const originalBox = measureViewportBoxForContent(
        block,
        originalText,
        typography,
        zoomScale
    );
    const displayBox = measureViewportBoxForContent(
        block,
        displayText,
        typography,
        zoomScale
    );
    const pad = PDF_TEXT_WHITOUT_PADDING;
    const left = Math.min(block.left, originalBox.left, displayBox.left) - pad;
    const top = Math.min(block.top, originalBox.top, displayBox.top) - pad;
    const right =
        Math.max(
            block.left + block.width,
            originalBox.left + originalBox.width,
            displayBox.left + displayBox.width
        ) + pad;
    const bottom =
        Math.max(
            block.top + block.height,
            originalBox.top + originalBox.height,
            displayBox.top + displayBox.height
        ) + pad;

    return {
        left,
        top,
        width: Math.max(right - left, 12),
        height: Math.max(bottom - top, 12),
        displayBox,
    };
};

const fillMaskCoverRects = (ctx, boxes, padding = 2) => {
    const list = (boxes || []).filter(Boolean);
    if (!list.length) return;
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    list.forEach((box) => {
        left = Math.min(left, box.left);
        top = Math.min(top, box.top);
        right = Math.max(right, box.left + box.width);
        bottom = Math.max(bottom, box.top + box.height);
    });
    ctx.fillStyle = PDF_EDIT_SURFACE;
    ctx.fillRect(
        left - padding,
        top - padding,
        Math.max(right - left + padding * 2, 8),
        Math.max(bottom - top + padding * 2, 6)
    );
};

const drawMaskPreviewText = (ctx, typography, box, text, textColor) => {
    const lines = String(text ?? "")
        .replace(/\r/g, "")
        .split("\n");
    ctx.fillStyle = colorToCss(textColor);
    ctx.font = canvasFontString(typography, box.fontSize);
    ctx.textBaseline = "top";
    const lineStep = box.fontSize * LINE_HEIGHT_FACTOR;
    lines.forEach((line, index) => {
        if (!String(line).length && index < lines.length - 1) return;
        ctx.fillText(String(line).length ? String(line) : " ", box.left, box.top + index * lineStep);
    });
};

const caretIndexFromTextareaPoint = (element, localX, localY) => {
    const text = String(element?.value ?? "");
    if (!text.length) {
        return 0;
    }
    const style = window.getComputedStyle(element);
    const padLeft = Number.parseFloat(style.paddingLeft) || 0;
    const padTop = Number.parseFloat(style.paddingTop) || 0;
    const x = Math.max(0, localX - padLeft);
    const y = Math.max(0, localY - padTop);
    const fontSize = Number.parseFloat(style.fontSize) || 16;
    const lineHeight =
        Number.parseFloat(style.lineHeight) || fontSize * LINE_HEIGHT_FACTOR;
    const lines = text.split(/\r?\n/);
    const lineIndex = Math.min(
        Math.max(0, Math.floor(y / Math.max(lineHeight, 1))),
        lines.length - 1
    );
    let offset = 0;
    for (let index = 0; index < lineIndex; index += 1) {
        offset += lines[index].length + 1;
    }
    const line = lines[lineIndex] ?? "";
    if (!line.length) {
        return Math.min(offset, text.length);
    }
    if (!sharedMeasureCanvasCtx) {
        const canvas = document.createElement("canvas");
        sharedMeasureCanvasCtx = canvas.getContext("2d");
    }
    sharedMeasureCanvasCtx.font = style.font;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index <= line.length; index += 1) {
        const width = sharedMeasureCanvasCtx.measureText(line.slice(0, index)).width;
        const distance = Math.abs(width - x);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
        }
    }
    return Math.min(offset + bestIndex, text.length);
};

const blockHasVisibleChange = (
    block,
    edits,
    activeBlockId,
    activeDraftText,
    activeSelectStyle
) => {
    const edit = edits[block.id];
    if (block.id === activeBlockId) {
        if (block.isCustom) {
            return true;
        }
        const baseline = resolveBlockDisplayText(block, edits);
        if (String(activeDraftText ?? "") !== baseline) {
            return true;
        }
        if (activeSelectStyle) {
            if (Number(activeSelectStyle.fontSize) !== Number(block.fontSize)) {
                return true;
            }
            if (!rgbChannelsEqual(activeSelectStyle.textColor, block.textColor)) {
                return true;
            }
            if (
                String(activeSelectStyle.pdfFontKey ?? "") !==
                String(block.pdfFontKey ?? "Helvetica")
            ) {
                return true;
            }
        }
        return false;
    }
    return Boolean(edit);
};

const PdfInlineTextEditor = ({
    sourceBytes,
    fileName = "document.pdf",
    saveButtonLabel = "Save PDF",
    flattenOnSave = false,
    onSave,
    onCancel,
}) => {
    const canvasRef = useRef(null);
    const maskCanvasRef = useRef(null);
    const textLayerRef = useRef(null);
    const stageRef = useRef(null);
    const renderTaskRef = useRef(null);
    const activeInputRef = useRef(null);
    const viewportRef = useRef(null);
    const justPlacedCustomRef = useRef(false);
    const pendingPlacementScrollRef = useRef(null);
    const lastCustomPlacementPointRef = useRef(null);
    const customDragSessionRef = useRef(null);
    const customDragMovedRef = useRef(false);
    const pendingTextCaretRef = useRef(null);
    const startEditingBlockRef = useRef(() => {});
    const pdfDocRef = useRef(null);
    const pdfLoadIdRef = useRef(0);
    const pageRenderIdRef = useRef(0);
    const workingBytesRef = useRef(null);
    const textExtractRevisionRef = useRef(0);
    const erasureReplacementsRef = useRef([]);
    const [erasureZones, setErasureZones] = useState([]);
    const pageTextBlocksCacheRef = useRef(new Map());

    const invalidateTextExtractCache = useCallback(() => {
        textExtractRevisionRef.current += 1;
        pageTextBlocksCacheRef.current.clear();
    }, []);

    const cancelActivePageRender = useCallback(() => {
        if (renderTaskRef.current) {
            try {
                renderTaskRef.current.cancel();
            } catch {
                // ignore
            }
            renderTaskRef.current = null;
        }
    }, []);

    const scrollStageToContentPoint = useCallback((contentX, contentY) => {
        const stage = stageRef.current;
        if (!stage) return;
        const maxTop = Math.max(0, stage.scrollHeight - stage.clientHeight);
        const maxLeft = Math.max(0, stage.scrollWidth - stage.clientWidth);
        stage.scrollTop = Math.min(
            maxTop,
            Math.max(0, contentY - stage.clientHeight * 0.35)
        );
        stage.scrollLeft = Math.min(
            maxLeft,
            Math.max(0, contentX - stage.clientWidth * 0.5)
        );
    }, []);

    const scrollStageToClientPoint = useCallback(
        (clientX, clientY) => {
            const stage = stageRef.current;
            if (!stage) return;
            const stageRect = stage.getBoundingClientRect();
            const contentX = clientX - stageRect.left + stage.scrollLeft;
            const contentY = clientY - stageRect.top + stage.scrollTop;
            scrollStageToContentPoint(contentX, contentY);
        },
        [scrollStageToContentPoint]
    );

    const captureOuterScrollPositions = useCallback((fromEl) => {
        const snapshots = [];
        const stage = stageRef.current;
        let node = fromEl?.parentElement;
        while (node) {
            if (node === stage) break;
            if (
                node.scrollHeight > node.clientHeight + 1 ||
                node.scrollWidth > node.clientWidth + 1
            ) {
                snapshots.push({
                    node,
                    scrollTop: node.scrollTop,
                    scrollLeft: node.scrollLeft,
                });
            }
            node = node.parentElement;
        }
        return snapshots;
    }, []);

    const applyPendingTextareaCaret = useCallback((inputEl) => {
        if (!inputEl || typeof inputEl.setSelectionRange !== "function") {
            return false;
        }
        const pending = pendingTextCaretRef.current;
        if (!pending) {
            return false;
        }
        pendingTextCaretRef.current = null;
        const rect = inputEl.getBoundingClientRect();
        const index = caretIndexFromTextareaPoint(
            inputEl,
            pending.clientX - rect.left,
            pending.clientY - rect.top
        );
        inputEl.setSelectionRange(index, index);
        return true;
    }, []);

    const focusActiveInputPreservingOuterScroll = useCallback(
        (alignStageToPlacement = false) => {
            const inputEl = activeInputRef.current;
            if (!inputEl || typeof inputEl.focus !== "function") return;

            const outerScroll = captureOuterScrollPositions(inputEl);
            const placement = alignStageToPlacement
                ? lastCustomPlacementPointRef.current
                : null;

            try {
                inputEl.focus({ preventScroll: true });
            } catch {
                inputEl.focus();
            }

            outerScroll.forEach(({ node, scrollTop, scrollLeft }) => {
                node.scrollTop = scrollTop;
                node.scrollLeft = scrollLeft;
            });

            if (placement) {
                scrollStageToContentPoint(placement.contentX, placement.contentY);
            }

            if (!applyPendingTextareaCaret(inputEl)) {
                const length = inputEl.value?.length ?? 0;
                inputEl.setSelectionRange(length, length);
            }
        },
        [
            applyPendingTextareaCaret,
            captureOuterScrollPositions,
            scrollStageToContentPoint,
        ]
    );

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pageCount, setPageCount] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
    const [textBlocks, setTextBlocks] = useState([]);
    const [edits, setEdits] = useState({});
    const editsRef = useRef(edits);
    const [activeBlockId, setActiveBlockId] = useState("");
    const [activeDraftText, setActiveDraftText] = useState("");
    const [activeSelectStyle, setActiveSelectStyle] = useState(null);
    const [addTextMode, setAddTextMode] = useState(false);
    const [customBlocks, setCustomBlocks] = useState([]);
    const [pageLayout, setPageLayout] = useState({ width: 0, height: 0 });
    const [customDragBlockId, setCustomDragBlockId] = useState("");
    const [pageThumbnails, setPageThumbnails] = useState([]);
    const [thumbsLoading, setThumbsLoading] = useState(false);
    const [workingBytes, setWorkingBytes] = useState(null);
    const [textExtractReady, setTextExtractReady] = useState(false);
    const [newTextToolStyle, setNewTextToolStyle] = useState(() => ({
        fontSize: DEFAULT_INSERT_FONT_SIZE,
        textColor: { r: 0, g: 0, b: 0 },
        ...fontMetaFromPdfKey("Helvetica"),
    }));

    useEffect(() => {
        workingBytesRef.current = workingBytes;
    }, [workingBytes]);

    useEffect(() => {
        editsRef.current = edits;
    }, [edits]);

    useEffect(() => {
        const pending = erasureReplacementsRef.current;
        if (!pending?.length || !textExtractReady || !textBlocks.length) {
            return;
        }
        const { zones } = buildBlankSlotsFromErasureReplacements(
            textBlocks,
            pending
        );
        if (!zones.length) {
            return;
        }
        const viewport = viewportRef.current;
        setErasureZones((previous) => {
            const next = [...previous];
            zones.forEach((zone) => {
                if (
                    !next.some(
                        (item) =>
                            item.page === zone.page &&
                            item.x === zone.x &&
                            item.y === zone.y &&
                            item.width === zone.width
                    )
                ) {
                    next.push(zone);
                }
            });
            return next;
        });
    }, [textBlocks, textExtractReady, pageNumber]);

    const zoom = ZOOM_LEVELS[zoomIndex] ?? ZOOM_LEVELS[DEFAULT_ZOOM_INDEX];

    const mergedTextBlocks = useMemo(() => {
        const pageNumbers = [...new Set(textBlocks.map((block) => block.page))];
        return pageNumbers.flatMap((page) =>
            mergeTextBlocksForEditor(textBlocks, edits, page, erasureZones)
        );
    }, [edits, erasureZones, textBlocks]);

    const visibleBlocks = useMemo(() => {
        const viewport = viewportRef.current;
        const hiddenIds = new Set(
            erasureZones.flatMap((zone) => zone.hiddenBlockIds || [])
        );
        const pageBlocks = mergedTextBlocks
            .filter(
                (block) =>
                    block.page === pageNumber &&
                    !hiddenIds.has(block.id) &&
                    !blockIsHiddenByErasureZone(block, erasureZones)
            )
            .map((block) => {
                const displayText = resolveBlockDisplayText(block, edits);
                if (!viewport) {
                    return { ...block, originalText: displayText };
                }
                return layoutBlockOnViewport(
                    { ...block, originalText: displayText },
                    viewport,
                    displayText
                );
            });
        const customs = customBlocks
            .filter((block) => block.page === pageNumber && !block.isBlankSlot)
            .map((block) => {
                const displayText =
                    block.id === activeBlockId
                        ? activeDraftText
                        : edits[block.id]?.newText || " ";
                return layoutBlockOnViewport(block, viewport, displayText);
            });
        return [...pageBlocks, ...customs];
    }, [
        activeBlockId,
        activeDraftText,
        customBlocks,
        edits,
        pageNumber,
        pageLayout.height,
        pageLayout.width,
        erasureZones,
        mergedTextBlocks,
        zoom,
    ]);

    const allBlocksForLookup = useMemo(
        () => [...mergedTextBlocks, ...customBlocks],
        [customBlocks, mergedTextBlocks]
    );

    const sourceBytesKey = useMemo(() => {
        if (!sourceBytes) return "";
        const view =
            sourceBytes instanceof Uint8Array
                ? sourceBytes
                : new Uint8Array(sourceBytes);
        return `${view.byteLength}-${view[0]}-${view[view.byteLength - 1]}`;
    }, [sourceBytes]);

    const activeBlock = useMemo(
        () => allBlocksForLookup.find((block) => block.id === activeBlockId) || null,
        [activeBlockId, allBlocksForLookup]
    );
    const activeBlockRef = useRef(activeBlock);
    activeBlockRef.current = activeBlock;

    const buildEditEntry = useCallback(
        (block, newText, stylePatch = null, editsMap = {}, allBlocks = []) => {
            const normalized = String(newText ?? "");
            const styleFields = editStyleFieldsFromPatch(block, stylePatch);
            const baselineText = resolveBlockDisplayText(block, editsMap);
            const pdfSourceText =
                block.extractedText ??
                (editsMap[block.id]?.originalText != null
                    ? String(editsMap[block.id].originalText)
                    : block.originalText);
            if (block.isCustom) {
                if (!normalized.trim()) return null;
            } else if (
                normalized === baselineText &&
                !styleFields.styleChanged &&
                !(
                    normalized === "" &&
                    String(pdfSourceText ?? "") !== normalized
                )
            ) {
                return null;
            }
            const type = blockTypography(
                block,
                stylePatch != null
                    ? styleFields
                    : styleFields.styleChanged
                      ? styleFields
                      : null
            );
            let entry = {
                page: block.page,
                originalText: block.isCustom ? "" : String(pdfSourceText ?? ""),
                newText: normalized,
                isInsert: Boolean(block.isCustom),
                x: block.pdfX,
                y: block.pdfY,
                width: block.pdfWidth,
                height: block.pdfHeight,
                fontSize: styleFields.fontSize,
                pdfFontKey: type.pdfFontKey,
                cssFontFamily: type.cssFontFamily,
                cssFontWeight: type.cssFontWeight,
                cssFontStyle: type.cssFontStyle,
                textColorR: styleFields.textColorR,
                textColorG: styleFields.textColorG,
                textColorB: styleFields.textColorB,
                fillColorR: block.fillColor?.r ?? 1,
                fillColorG: block.fillColor?.g ?? 1,
                fillColorB: block.fillColor?.b ?? 1,
                styleChanged: styleFields.styleChanged,
            };
            if (!block.isCustom && normalized === "" && allBlocks.length) {
                entry = expandEraseEditEntry(block, allBlocks, entry);
            }
            return entry;
        },
        []
    );

    const loadDocument = useCallback(
        async ({ resetSession = true, bytesOverride = null, silent = false } = {}) => {
            const byteSource = bytesOverride ?? workingBytesRef.current ?? sourceBytes;
            if (!byteSource) return;

            const loadId = pdfLoadIdRef.current + 1;
            pdfLoadIdRef.current = loadId;
            cancelActivePageRender();

            if (!silent) {
                setLoading(true);
            }
            try {
                const data =
                    byteSource instanceof Uint8Array
                        ? new Uint8Array(byteSource)
                        : new Uint8Array(byteSource);

                const previousDoc = pdfDocRef.current;
                const doc = await pdfjs.getDocument(buildPdfJsDocumentInit(data)).promise;
                if (loadId !== pdfLoadIdRef.current) {
                    doc.destroy();
                    return;
                }

                if (previousDoc && previousDoc !== doc) {
                    previousDoc.destroy();
                }

                pdfDocRef.current = doc;
                setPdfDoc(doc);
                setPageCount(doc.numPages);
                if (resetSession) {
                    erasureReplacementsRef.current = [];
                    setErasureZones([]);
                    setPageNumber(1);
                    setEdits({});
                    setCustomBlocks([]);
                    setAddTextMode(false);
                    setActiveBlockId("");
                    setActiveDraftText("");
                    setActiveSelectStyle(null);
                }
            } catch (error) {
                if (loadId === pdfLoadIdRef.current) {
                    pdfDocRef.current = null;
                    setPdfDoc(null);
                    toast.error(error?.message || "Unable to load PDF for editing");
                }
            } finally {
                if (loadId === pdfLoadIdRef.current && !silent) {
                    setLoading(false);
                }
            }
        },
        [cancelActivePageRender, sourceBytes]
    );

    useEffect(() => {
        if (!sourceBytes) {
            setWorkingBytes(null);
            pdfDocRef.current = null;
            setPdfDoc(null);
            return undefined;
        }

        const normalized =
            sourceBytes instanceof Uint8Array
                ? new Uint8Array(sourceBytes)
                : new Uint8Array(sourceBytes);

        const alreadyLoaded =
            workingBytesRef.current &&
            workingBytesRef.current.byteLength === normalized.byteLength &&
            workingBytesRef.current.every((byte, index) => byte === normalized[index]);

        if (!alreadyLoaded) {
            invalidateTextExtractCache();
            setWorkingBytes(normalized);
            loadDocument({ resetSession: true, bytesOverride: normalized });
        }

        return () => {
            pdfLoadIdRef.current += 1;
            cancelActivePageRender();
        };
    }, [
        cancelActivePageRender,
        invalidateTextExtractCache,
        loadDocument,
        sourceBytesKey,
    ]);

    useEffect(() => {
        if (!pdfDoc) {
            setPageThumbnails([]);
            return undefined;
        }

        let cancelled = false;
        setThumbsLoading(true);
        const thumbLoadId = pdfLoadIdRef.current;

        const buildThumbnails = async () => {
            try {
                const thumbs = [];
                for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
                    if (cancelled || thumbLoadId !== pdfLoadIdRef.current) {
                        return;
                    }
                    const page = await pdfDoc.getPage(pageNum);
                    const baseViewport = page.getViewport({ scale: 1 });
                    const scale = THUMB_MAX_WIDTH / baseViewport.width;
                    const viewport = page.getViewport({ scale });
                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    const context = canvas.getContext("2d");
                    if (!context) continue;
                    await page.render({ canvasContext: context, viewport }).promise;
                    thumbs.push({
                        page: pageNum,
                        url: canvas.toDataURL("image/jpeg", 0.82),
                        width: viewport.width,
                        height: viewport.height,
                    });
                }
                if (!cancelled) {
                    setPageThumbnails(thumbs);
                }
            } catch {
                if (!cancelled) {
                    setPageThumbnails([]);
                }
            } finally {
                if (!cancelled) {
                    setThumbsLoading(false);
                }
            }
        };

        buildThumbnails();
        return () => {
            cancelled = true;
        };
    }, [pdfDoc, sourceBytesKey]);

    useEffect(
        () => () => {
            cancelActivePageRender();
            if (pdfDocRef.current) {
                pdfDocRef.current.destroy();
                pdfDocRef.current = null;
            }
        },
        [cancelActivePageRender]
    );

    const renderPage = useCallback(async () => {
        const doc = pdfDocRef.current;
        if (!doc || !canvasRef.current || !textLayerRef.current || !maskCanvasRef.current) {
            return;
        }

        const renderId = pageRenderIdRef.current + 1;
        pageRenderIdRef.current = renderId;
        setTextExtractReady(false);
        cancelActivePageRender();

        const page = await doc.getPage(pageNumber);
        if (renderId !== pageRenderIdRef.current) return;

        const viewport = page.getViewport({ scale: zoom });
        const canvas = canvasRef.current;
        const maskCanvas = maskCanvasRef.current;
        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        maskCanvas.height = viewport.height;
        maskCanvas.width = viewport.width;
        maskCanvas.style.width = `${viewport.width}px`;
        maskCanvas.style.height = `${viewport.height}px`;

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = renderTask;
        try {
            await renderTask.promise;
        } catch (error) {
            if (error?.name === "RenderingCancelledException") return;
            throw error;
        } finally {
            if (renderTaskRef.current === renderTask) {
                renderTaskRef.current = null;
            }
        }

        if (renderId !== pageRenderIdRef.current) return;

        const extractCacheKey = `${textExtractRevisionRef.current}-${pageNumber}`;
        let combinedBlocks = pageTextBlocksCacheRef.current.get(extractCacheKey);

        if (!combinedBlocks) {
            await page.getOperatorList();
            const textContent = await page.getTextContent({
                includeMarkedContent: true,
                disableNormalization: false,
            });
            let annotations = [];
            try {
                annotations = await page.getAnnotations({ intent: "display" });
            } catch {
                try {
                    annotations = await page.getAnnotations();
                } catch {
                    annotations = [];
                }
            }
            if (renderId !== pageRenderIdRef.current) return;

            let extractedBlocks = buildEditableTextBlocks(
                textContent.items,
                pageNumber,
                page,
                textContent.styles || {}
            );
            const formBlocks = buildEditableBlocksFromWidgetAnnotations(
                annotations,
                pageNumber
            );
            combinedBlocks = mergeExtractedAndFormBlocks(extractedBlocks, formBlocks);

            if (combinedBlocks.length === 0 && workingBytesRef.current) {
                try {
                    const bytes = workingBytesRef.current;
                    let pureItems = [];
                    try {
                        const singlePageBytes = await saveSinglePagePdfBytes(
                            bytes,
                            pageNumber
                        );
                        pureItems = await extractTextItemsFromPdfBytes(singlePageBytes);
                    } catch {
                        pureItems = [];
                    }
                    if (!pureItems.length && doc.numPages === 1) {
                        pureItems = await extractTextItemsFromPdfBytes(bytes);
                    }
                    if (pureItems.length) {
                        extractedBlocks = buildEditableTextBlocks(
                            pureItems,
                            pageNumber,
                            null,
                            {}
                        );
                        combinedBlocks = mergeExtractedAndFormBlocks(
                            extractedBlocks,
                            formBlocks
                        );
                    }
                } catch {
                    // optional fallback
                }
            }

            combinedBlocks = dedupeOverlappingExtractedBlocks(combinedBlocks);
            if (renderId !== pageRenderIdRef.current) return;
            pageTextBlocksCacheRef.current.set(extractCacheKey, combinedBlocks);
        }

        const blocks = combinedBlocks.map((block) => {
            const box = toViewportBox(block, viewport);
            const colors = sampleTextAndFillColors(context, box);
            return { ...block, ...box, ...colors };
        });

        viewportRef.current = viewport;
        setPageLayout({ width: viewport.width, height: viewport.height });
        setTextBlocks(blocks);
        setTextExtractReady(true);
        textLayerRef.current.style.width = `${viewport.width}px`;
        textLayerRef.current.style.height = `${viewport.height}px`;
    }, [cancelActivePageRender, pageNumber, zoom]);

    useEffect(() => {
        if (!pdfDoc || loading) return undefined;

        let cancelled = false;
        const run = async () => {
            if (cancelled || loading) return;
            try {
                await renderPage();
            } catch (error) {
                if (cancelled || error?.name === "RenderingCancelledException") return;
                toast.error("Unable to render PDF page");
            }
        };
        run();
        return () => {
            cancelled = true;
            pageRenderIdRef.current += 1;
            cancelActivePageRender();
        };
    }, [cancelActivePageRender, loading, pageNumber, pdfDoc, renderPage, zoom]);

    const paintEditMasks = useCallback(() => {
        const maskCanvas = maskCanvasRef.current;
        if (!maskCanvas) return;

        const ctx = maskCanvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);

        visibleBlocks.forEach((block) => {
            const edit = edits[block.id];
            const isActive = block.id === activeBlockId;
            const isInsert = Boolean(block.isCustom || edit?.isInsert);
            const hasVisibleChange = blockHasVisibleChange(
                block,
                edits,
                activeBlockId,
                activeDraftText,
                activeSelectStyle
            );
            const shouldWhiteoutWhileEditing = isActive && !block.isCustom;

            if (!hasVisibleChange && !shouldWhiteoutWhileEditing) {
                return;
            }

            const previewText = isActive
                ? block.isCustom
                    ? String(activeDraftText ?? "")
                    : String(activeDraftText ?? "").replace(/\r?\n/g, " ")
                : resolveBlockDisplayText(block, edits);
            if (
                !previewText.trim() &&
                !isActive &&
                !shouldWhiteoutWhileEditing &&
                !edit
            ) {
                return;
            }
            const pdfEraseLabel =
                edit?.originalText ??
                block.extractedText ??
                block.originalText ??
                "";
            const activeStyleEdit =
                isActive && !block.isCustom && activeSelectStyle
                    ? {
                          fontSize: activeSelectStyle.fontSize,
                          textColorR: activeSelectStyle.textColor?.r ?? 0,
                          textColorG: activeSelectStyle.textColor?.g ?? 0,
                          textColorB: activeSelectStyle.textColor?.b ?? 0,
                          pdfFontKey: activeSelectStyle.pdfFontKey,
                          cssFontFamily: activeSelectStyle.cssFontFamily,
                          cssFontWeight: activeSelectStyle.cssFontWeight,
                          cssFontStyle: activeSelectStyle.cssFontStyle,
                      }
                    : isActive && block.isCustom
                      ? styleEditShapeFromBlock(block)
                      : null;
            const activeFontKey =
                activeStyleEdit?.pdfFontKey ?? edit?.pdfFontKey ?? block.pdfFontKey;
            const paintBlock =
                isActive && block.isCustom
                    ? {
                          ...block,
                          textColor: {
                              r: block.textColor?.r ?? 0,
                              g: block.textColor?.g ?? 0,
                              b: block.textColor?.b ?? 0,
                          },
                      }
                    : {
                          ...block,
                          fontSize:
                              activeStyleEdit?.fontSize ?? edit?.fontSize ?? block.fontSize,
                          textColor: activeStyleEdit
                              ? {
                                    r: activeStyleEdit.textColorR,
                                    g: activeStyleEdit.textColorG,
                                    b: activeStyleEdit.textColorB,
                                }
                              : block.textColor,
                          pdfFontKey: activeFontKey,
                          ...(activeStyleEdit?.cssFontFamily
                              ? {
                                    cssFontFamily: activeStyleEdit.cssFontFamily,
                                    cssFontWeight: activeStyleEdit.cssFontWeight,
                                    cssFontStyle: activeStyleEdit.cssFontStyle,
                                }
                              : fontMetaFromPdfKey(activeFontKey)),
                      };
            const typography = blockTypography(
                paintBlock,
                isActive ? activeStyleEdit : edit
            );
            const maskPreviewLabel = previewText.trim() ? previewText : " ";
            const viewport = viewportRef.current;
            const expandedPdfErase =
                !isInsert &&
                Boolean(edit) &&
                !String(previewText ?? "").trim() &&
                !shouldWhiteoutWhileEditing
                    ? {
                          pdfX: edit.x ?? paintBlock.pdfX,
                          pdfWidth: edit.width ?? paintBlock.pdfWidth,
                      }
                    : null;
            const maskLayoutBlock =
                expandedPdfErase && viewport
                    ? layoutBlockOnViewport(
                          { ...paintBlock, ...expandedPdfErase },
                          viewport,
                          pdfEraseLabel || " "
                      )
                    : paintBlock;
            const maskRect = maskRectCoveringText(
                maskLayoutBlock,
                typography,
                isInsert ? "" : pdfEraseLabel,
                maskPreviewLabel,
                zoom
            );
            const box = maskRect.displayBox;
            const textColor = isActive
                ? resolveTextColor(paintBlock, activeStyleEdit)
                : resolveTextColor(paintBlock, edit);

            if (isActive && block.isCustom) {
                return;
            }

            const hasPreviewText = Boolean(String(previewText ?? "").trim());

            if (shouldWhiteoutWhileEditing) {
                const originalCoverBox = measureViewportBoxForContent(
                    paintBlock,
                    pdfEraseLabel || " ",
                    typography,
                    zoom
                );
                const draftCoverBox = measureViewportBoxForContent(
                    paintBlock,
                    maskPreviewLabel,
                    typography,
                    zoom
                );
                fillMaskCoverRects(
                    ctx,
                    hasPreviewText
                        ? [originalCoverBox, draftCoverBox]
                        : [originalCoverBox],
                    2
                );
                return;
            }

            if (block.isBlankSlot) {
                return;
            }

            if (!isInsert) {
                if (!hasPreviewText) {
                    ctx.fillStyle = PDF_EDIT_SURFACE;
                    ctx.fillRect(
                        maskRect.left,
                        maskRect.top,
                        maskRect.width,
                        maskRect.height
                    );
                    return;
                }
                const originalCoverBox = measureViewportBoxForContent(
                    paintBlock,
                    pdfEraseLabel || " ",
                    typography,
                    zoom
                );
                fillMaskCoverRects(ctx, [originalCoverBox, box], 2);
                drawMaskPreviewText(ctx, typography, box, maskPreviewLabel, textColor);
                return;
            }

            if (!hasPreviewText) {
                return;
            }

            drawMaskPreviewText(ctx, typography, box, previewText, textColor);
        });

        erasureZones
            .filter((zone) => zone.page === pageNumber)
            .forEach((zone) => {
                const viewport = viewportRef.current;
                if (!viewport) return;
                const layoutSeed = {
                    page: zone.page,
                    pdfX: zone.x,
                    pdfY: zone.y,
                    pdfWidth: zone.width,
                    pdfHeight: zone.height,
                    fontSize: zone.fontSize,
                    pdfFontKey: zone.pdfFontKey,
                    cssFontFamily: zone.cssFontFamily,
                    cssFontWeight: zone.cssFontWeight,
                    cssFontStyle: zone.cssFontStyle,
                };
                const laidOut = layoutBlockOnViewport(
                    layoutSeed,
                    viewport,
                    zone.originalText || " "
                );
                const typography = blockTypography(laidOut, null);
                const maskRect = maskRectCoveringText(
                    laidOut,
                    typography,
                    zone.originalText || " ",
                    " ",
                    zoom
                );
                ctx.fillStyle = PDF_EDIT_SURFACE;
                ctx.fillRect(
                    maskRect.left - 4,
                    maskRect.top - 4,
                    maskRect.width + 8,
                    maskRect.height + 8
                );
            });
    }, [
        activeBlockId,
        activeDraftText,
        activeSelectStyle,
        edits,
        erasureZones,
        pageNumber,
        visibleBlocks,
        zoom,
    ]);

    useEffect(() => {
        paintEditMasks();
    }, [paintEditMasks]);

    useLayoutEffect(() => {
        const pendingScroll = pendingPlacementScrollRef.current;
        if (pendingScroll?.contentX != null && pendingScroll?.contentY != null) {
            scrollStageToContentPoint(pendingScroll.contentX, pendingScroll.contentY);
            pendingPlacementScrollRef.current = null;
        } else if (pendingScroll?.clientX != null && pendingScroll?.clientY != null) {
            scrollStageToClientPoint(pendingScroll.clientX, pendingScroll.clientY);
            pendingPlacementScrollRef.current = null;
        }

        if (!activeBlockId) return undefined;

        if (isToolbarFocusTarget(document.activeElement)) {
            return undefined;
        }

        const block = activeBlockRef.current;
        const alignStageToPlacement = Boolean(
            justPlacedCustomRef.current && block?.isCustom
        );
        const focusInput = () => {
            if (isToolbarFocusTarget(document.activeElement)) {
                return;
            }
            if (block?.isCustom) {
                focusActiveInputPreservingOuterScroll(alignStageToPlacement);
                return;
            }
            const inputEl = activeInputRef.current;
            if (!inputEl || typeof inputEl.focus !== "function") return;
            try {
                inputEl.focus({ preventScroll: true });
            } catch {
                inputEl.focus();
            }
            if (!applyPendingTextareaCaret(inputEl)) {
                const length = inputEl.value?.length ?? 0;
                if (typeof inputEl.setSelectionRange === "function") {
                    inputEl.setSelectionRange(length, length);
                }
            }
        };
        focusInput();
        const frame = window.requestAnimationFrame(focusInput);
        const frame2 = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(focusInput);
        });
        return () => {
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(frame2);
        };
    }, [
        activeBlockId,
        applyPendingTextareaCaret,
        focusActiveInputPreservingOuterScroll,
        scrollStageToClientPoint,
        scrollStageToContentPoint,
    ]);

    const startEditingBlock = (block, event = null) => {
        const hiddenIds = new Set(
            erasureZones.flatMap((zone) => zone.hiddenBlockIds || [])
        );
        if (
            !block ||
            block.isBlankSlot ||
            hiddenIds.has(block.id) ||
            blockIsHiddenByErasureZone(block, erasureZones) ||
            isErasedEdit(edits[block.id]) ||
            (!block.isCustom && !String(resolveBlockDisplayText(block, edits)).trim())
        ) {
            return;
        }
        if (event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
            pendingTextCaretRef.current = {
                clientX: event.clientX,
                clientY: event.clientY,
                blockId: block.id,
            };
        } else {
            pendingTextCaretRef.current = null;
        }
        const edited = edits[block.id];
        if (block.isCustom && edited) {
            const viewport = viewportRef.current;
            setCustomBlocks((previous) =>
                previous.map((item) => {
                    if (item.id !== block.id) return item;
                    const fontKey = edited.pdfFontKey || item.pdfFontKey;
                    const fontMeta = fontMetaFromPdfKey(fontKey);
                    const styled = {
                        ...item,
                        fontSize: edited.fontSize ?? item.fontSize,
                        textColor: resolveTextColor(item, edited),
                        pdfFontKey: fontKey,
                        cssFontFamily: edited.cssFontFamily ?? fontMeta.cssFontFamily,
                        cssFontWeight: edited.cssFontWeight ?? fontMeta.cssFontWeight,
                        cssFontStyle: edited.cssFontStyle ?? fontMeta.cssFontStyle,
                    };
                    if (!viewport) return styled;
                    const laidOut = layoutBlockOnViewport(
                        styled,
                        viewport,
                        edited.newText || " "
                    );
                    return pinCustomBlockPlacement(item, laidOut);
                })
            );
        }
        setActiveBlockId(block.id);
        const draftSeed = resolveBlockDisplayText(block, edits);
        setActiveDraftText(
            block.isCustom
                ? draftSeed
                : String(draftSeed ?? "").replace(/\r?\n/g, " ")
        );
        if (!block.isCustom) {
            const patch = stylePatchFromEdit(block, edited);
            const fontKey =
                patch?.pdfFontKey ?? edited?.pdfFontKey ?? block.pdfFontKey ?? "Helvetica";
            setActiveSelectStyle({
                fontSize: patch?.fontSize ?? block.fontSize,
                textColor: { ...(patch?.textColor ?? block.textColor ?? { r: 0, g: 0, b: 0 }) },
                ...fontMetaFromPdfKey(fontKey),
            });
        } else {
            setActiveSelectStyle(null);
        }
    };
    startEditingBlockRef.current = startEditingBlock;

    const cancelActiveEdit = useCallback(() => {
        const blockId = activeBlockId;
        if (blockId) {
            const block = customBlocks.find((item) => item.id === blockId);
            if (block?.isCustom && !editsRef.current[blockId]) {
                setCustomBlocks((previous) =>
                    previous.filter((item) => item.id !== blockId)
                );
            }
        }
        setActiveBlockId("");
        setActiveDraftText("");
        setActiveSelectStyle(null);
    }, [activeBlockId, customBlocks]);

    const removeCustomBlock = (blockId) => {
        setCustomBlocks((previous) => previous.filter((block) => block.id !== blockId));
    };

    const handlePlaceNewText = useCallback(
        (event) => {
            if (!addTextMode || !viewportRef.current || !canvasRef.current) return;

            const canvas = canvasRef.current;
            const layerEl = textLayerRef.current || canvas;
            const rect = layerEl.getBoundingClientRect();
            const viewportX = event.clientX - rect.left;
            const viewportY = event.clientY - rect.top;
            if (
                viewportX < 0 ||
                viewportY < 0 ||
                viewportX > rect.width ||
                viewportY > rect.height
            ) {
                return;
            }

            const viewport = viewportRef.current;
            const fontSize = Math.min(
                MAX_CUSTOM_FONT_SIZE,
                Math.max(
                    MIN_CUSTOM_FONT_SIZE,
                    Number(newTextToolStyle.fontSize) || DEFAULT_INSERT_FONT_SIZE
                )
            );
            const fontHeightViewport = fontSize * zoom;
            const [pdfX, pdfY] = viewport.convertToPdfPoint(
                viewportX,
                viewportY + fontHeightViewport
            );

            const seedBlock = {
                originalText: "",
                fontSize,
                pdfX,
                pdfY,
                pdfWidth: fontSize * 2,
                pdfHeight: fontSize * 1.15,
            };
            const box = toViewportBox(seedBlock, viewport);
            const canvasContext = canvas.getContext("2d");
            const pickedColor = newTextToolStyle.textColor || { r: 0, g: 0, b: 0 };
            const colors = {
                textColor: pickedColor,
                fillColor: canvasContext
                    ? sampleTextAndFillColors(canvasContext, box).fillColor
                    : { r: 1, g: 1, b: 1 },
            };

            const newBlock = {
                id: `custom-p${pageNumber}-${Date.now()}`,
                page: pageNumber,
                isCustom: true,
                originalText: "",
                fontSize,
                pdfX,
                pdfY,
                pdfWidth: seedBlock.pdfWidth,
                pdfHeight: seedBlock.pdfHeight,
                placementViewportX: viewportX,
                placementViewportY: viewportY,
                pdfFontKey: newTextToolStyle.pdfFontKey || "Helvetica",
                cssFontFamily:
                    newTextToolStyle.cssFontFamily || "Helvetica, Arial, sans-serif",
                cssFontWeight: newTextToolStyle.cssFontWeight || "400",
                cssFontStyle: newTextToolStyle.cssFontStyle || "normal",
                ...box,
                ...colors,
            };

            pendingTextCaretRef.current = {
                clientX: event.clientX,
                clientY: event.clientY,
                blockId: newBlock.id,
            };

            justPlacedCustomRef.current = true;
            window.setTimeout(() => {
                justPlacedCustomRef.current = false;
            }, 600);

            const placementPoint = { contentX: viewportX, contentY: viewportY };
            lastCustomPlacementPointRef.current = placementPoint;
            pendingPlacementScrollRef.current = placementPoint;
            scrollStageToContentPoint(viewportX, viewportY);

            setAddTextMode(false);
            setCustomBlocks((previous) => [...previous, newBlock]);
            setActiveBlockId(newBlock.id);
            setActiveDraftText("");
        },
        [addTextMode, newTextToolStyle, pageNumber, scrollStageToContentPoint, textBlocks, zoom]
    );

    const stylePatchFromCustomBlock = useCallback((block) => {
        if (!block?.isCustom) {
            return null;
        }
        return {
            fontSize: block.fontSize,
            textColor: resolveTextColor(block, editsRef.current[block.id]),
            pdfFontKey: block.pdfFontKey,
            cssFontFamily: block.cssFontFamily,
            cssFontWeight: block.cssFontWeight,
            cssFontStyle: block.cssFontStyle,
        };
    }, []);

    const stylePatchForBlock = useCallback(
        (block) => {
            if (block.id !== activeBlockId) {
                return null;
            }
            if (block.isCustom) {
                return stylePatchFromCustomBlock(block);
            }
            return activeSelectStyle;
        },
        [activeBlockId, activeSelectStyle, stylePatchFromCustomBlock]
    );

    const commitEdit = useCallback(
        (block, newText, stylePatchOverride = null) => {
            const previous = editsRef.current;
            const stylePatch = stylePatchOverride ?? stylePatchForBlock(block);
            const entry = buildEditEntry(
                block,
                newText,
                stylePatch,
                previous,
                allBlocksForLookup
            );
            const next = { ...previous };
            if (!entry) {
                delete next[block.id];
            } else {
                next[block.id] = entry;
            }
            editsRef.current = next;
            setEdits(next);
            if (block.isCustom && !entry) {
                removeCustomBlock(block.id);
            }
        },
        [allBlocksForLookup, buildEditEntry, stylePatchForBlock]
    );

    const commitPdfTextErase = useCallback(
        (block) => {
            let entry = buildEditEntry(
                block,
                "",
                stylePatchForBlock(block),
                editsRef.current,
                allBlocksForLookup
            );
            if (!entry && !block.isCustom) {
                entry = expandEraseEditEntry(block, allBlocksForLookup, {
                    page: block.page,
                    originalText: String(block.extractedText ?? block.originalText ?? ""),
                    newText: "",
                    isInsert: false,
                    x: block.pdfX,
                    y: block.pdfY,
                    width: block.pdfWidth,
                    height: block.pdfHeight,
                    fontSize: block.fontSize,
                    pdfFontKey: block.pdfFontKey,
                    cssFontFamily: block.cssFontFamily,
                    cssFontWeight: block.cssFontWeight,
                    cssFontStyle: block.cssFontStyle,
                    textColorR: block.textColor?.r ?? 0,
                    textColorG: block.textColor?.g ?? 0,
                    textColorB: block.textColor?.b ?? 0,
                    fillColorR: block.fillColor?.r ?? 1,
                    fillColorG: block.fillColor?.g ?? 1,
                    fillColorB: block.fillColor?.b ?? 1,
                    styleChanged: false,
                });
            }
            if (!entry) return;

            const source =
                textBlocks.find((item) => item.id === block.id) || block;
            const run = hiddenPdfBlockIdsForErase(source, allBlocksForLookup);
            const runBlocks = allBlocksForLookup.filter((item) => run.includes(item.id));
            let minX = source.pdfX;
            let maxRight = source.pdfX + (source.pdfWidth || 0);
            let minY = source.pdfY;
            let maxHeight = source.pdfHeight || source.fontSize;
            runBlocks.forEach((item) => {
                minX = Math.min(minX, item.pdfX);
                maxRight = Math.max(maxRight, item.pdfX + (item.pdfWidth || 0));
                minY = Math.min(minY, item.pdfY);
                maxHeight = Math.max(maxHeight, item.pdfHeight || item.fontSize);
            });
            const hiddenIds = run;
            const zone = erasureZoneFromEditEntry(
                {
                    ...entry,
                    x: minX,
                    y: source.pdfY,
                    width: Math.max(maxRight - minX, entry.width || source.pdfWidth || 0),
                    height: Math.max(maxHeight, entry.height || source.pdfHeight || 0),
                    originalText: String(
                        source.extractedText ?? source.originalText ?? entry.originalText ?? ""
                    ),
                    fontSize: source.fontSize ?? entry.fontSize,
                },
                source,
                hiddenIds
            );

            setErasureZones((previous) => [...previous, zone]);
            setEdits((previous) => {
                const next = { ...previous };
                next[block.id] = { ...entry, newText: "" };
                hiddenIds.forEach((id) => {
                    if (id === block.id) return;
                    const sibling = allBlocksForLookup.find((item) => item.id === id);
                    next[id] = {
                        ...entry,
                        newText: "",
                        x: sibling?.pdfX ?? entry.x,
                        y: sibling?.pdfY ?? entry.y,
                        width: sibling?.pdfWidth ?? entry.width,
                        height: sibling?.pdfHeight ?? entry.height,
                        originalText: String(
                            sibling?.extractedText ?? sibling?.originalText ?? entry.originalText
                        ),
                    };
                });
                editsRef.current = next;
                return next;
            });
        },
        [allBlocksForLookup, buildEditEntry, stylePatchForBlock]
    );

    const finalizeCustomBlockLayout = useCallback(
        (block, displayText) => {
            const viewport = viewportRef.current;
            if (!viewport) {
                return;
            }
            setCustomBlocks((previous) =>
                previous.map((item) => {
                    if (item.id !== block.id) {
                        return item;
                    }
                    const laidOut = layoutBlockOnViewport(
                        item,
                        viewport,
                        displayText || " "
                    );
                    return pinCustomBlockPlacement(item, laidOut);
                })
            );
        },
        []
    );

    const applyExistingBlockText = useCallback(
        (block, text) => {
            const normalized = String(text ?? "");
            const latestBlock =
                allBlocksForLookup.find((item) => item.id === block.id) || block;
            const stylePatch =
                stylePatchForBlock(latestBlock) ??
                stylePatchFromEdit(latestBlock, editsRef.current[latestBlock.id]);
            if (!normalized.trim()) {
                commitPdfTextErase(latestBlock);
                cancelActiveEdit();
                return;
            }
            commitEdit(latestBlock, normalized, stylePatch);
            cancelActiveEdit();
            // toast.success("Text applied on the page — use Save PDF when finished");
        },
        [
            allBlocksForLookup,
            cancelActiveEdit,
            commitEdit,
            commitPdfTextErase,
            stylePatchForBlock,
        ]
    );

    const applyCustomBlockText = useCallback(
        (block, text) => {
            const normalized = String(text ?? "");
            if (!normalized.trim()) {
                setEdits((previous) => {
                    const next = { ...previous };
                    delete next[block.id];
                    editsRef.current = next;
                    return next;
                });
                if (!block.erasureZoneId) {
                    removeCustomBlock(block.id);
                }
                cancelActiveEdit();
                return;
            }
            const latestBlock =
                allBlocksForLookup.find((item) => item.id === block.id) || block;
            commitEdit(
                latestBlock,
                normalized,
                stylePatchFromCustomBlock(latestBlock)
            );
            finalizeCustomBlockLayout(latestBlock, normalized);
            if (latestBlock.isBlankSlot) {
                setCustomBlocks((previous) =>
                    previous.map((item) =>
                        item.id === latestBlock.id
                            ? { ...item, isBlankSlot: false }
                            : item
                    )
                );
            }
            cancelActiveEdit();
            // toast.success("Text applied on the page — use Save PDF when finished");
        },
        [
            allBlocksForLookup,
            cancelActiveEdit,
            commitEdit,
            finalizeCustomBlockLayout,
            stylePatchFromCustomBlock,
        ]
    );

    const handleInlineTextBlur = useCallback(
        (block, value, event) => {
            const normalized = String(value ?? "");
            if (isToolbarFocusTarget(event.relatedTarget)) {
                return;
            }
            if (block.isCustom) {
                applyCustomBlockText(block, normalized);
                return;
            }
            applyExistingBlockText(block, normalized);
        },
        [applyCustomBlockText, applyExistingBlockText]
    );

    const refocusActiveTextInput = useCallback(() => {
        if (!activeBlockId) return;
        window.requestAnimationFrame(() => {
            const inputEl = activeInputRef.current;
            if (!inputEl || typeof inputEl.focus !== "function") return;
            try {
                inputEl.focus({ preventScroll: true });
            } catch {
                inputEl.focus();
            }
        });
    }, [activeBlockId]);

    const updateActiveSelectStyle = useCallback((patch) => {
        setActiveSelectStyle((previous) => {
            const block = activeBlockRef.current;
            const base =
                previous ??
                (block && !block.isCustom
                    ? {
                          fontSize: block.fontSize,
                          textColor: { ...(block.textColor ?? { r: 0, g: 0, b: 0 }) },
                          ...fontMetaFromPdfKey(block.pdfFontKey ?? "Helvetica"),
                      }
                    : null);
            if (!base) return previous;
            const next = { ...base, ...patch };
            if (patch.textColor) {
                next.textColor = { ...patch.textColor };
            }
            if (patch.pdfFontKey && !patch.cssFontFamily) {
                Object.assign(next, fontMetaFromPdfKey(patch.pdfFontKey));
            }
            return next;
        });
    }, []);

    const updateActiveCustomBlock = useCallback(
        (patch) => {
            if (!activeBlockId) return;
            setCustomBlocks((previous) =>
                previous.map((block) => {
                    if (block.id !== activeBlockId) return block;
                    let next = { ...block, ...patch };
                    if (patch.pdfFontKey && patch.cssFontFamily == null) {
                        next = { ...next, ...fontMetaFromPdfKey(patch.pdfFontKey) };
                    }
                    const displayText = activeDraftText || edits[block.id]?.newText || " ";
                    const viewport = viewportRef.current;
                    if (!viewport) return next;
                    const laidOut = layoutBlockOnViewport(next, viewport, displayText);
                    return pinCustomBlockPlacement(block, laidOut);
                })
            );
        },
        [activeBlockId, activeDraftText, edits]
    );

    const handleCustomDraftChange = useCallback(
        (value) => {
            setActiveDraftText(value);
            const blockId = activeBlockId;
            if (!blockId) return;
            setCustomBlocks((previous) =>
                previous.map((item) => {
                    if (item.id !== blockId) return item;
                    const viewport = viewportRef.current;
                    if (!viewport) return item;
                    const laidOut = layoutBlockOnViewport(item, viewport, value || " ");
                    return pinCustomBlockPlacement(item, laidOut);
                })
            );
        },
        [activeBlockId]
    );

    const fitActiveTextInputToContent = useCallback(() => {
        const inputEl = activeInputRef.current;
        const block = activeBlockRef.current;
        if (!inputEl || !block) return;
        inputEl.style.height = "0px";
        const nextHeight = Math.max(inputEl.scrollHeight, 1);
        inputEl.style.height = `${nextHeight}px`;
    }, []);

    useLayoutEffect(() => {
        if (!activeBlockId || !activeBlockRef.current?.isCustom) return;
        fitActiveTextInputToContent();
    }, [
        activeBlockId,
        activeBlock?.fontSize,
        activeBlock?.pdfFontKey,
        activeDraftText,
        fitActiveTextInputToContent,
    ]);

    const getCustomDisplayText = useCallback(
        (block) => {
            if (block.id === activeBlockId) {
                return activeDraftText;
            }
            return edits[block.id]?.newText || "";
        },
        [activeBlockId, activeDraftText, edits]
    );

    const syncCustomEditPosition = useCallback(
        (block) => {
            const text = getCustomDisplayText(block);
            if (!String(text).trim()) return;
            const entry = buildEditEntry(
                block,
                text,
                stylePatchFromCustomBlock(block),
                editsRef.current,
                allBlocksForLookup
            );
            if (!entry) return;
            const next = { ...editsRef.current, [block.id]: entry };
            editsRef.current = next;
            setEdits(next);
        },
        [allBlocksForLookup, buildEditEntry, getCustomDisplayText, stylePatchFromCustomBlock]
    );

    const beginCustomBlockDrag = useCallback((block, event, box) => {
        if (!block.isCustom) return;
        event.preventDefault();
        event.stopPropagation();
        customDragMovedRef.current = false;
        customDragSessionRef.current = {
            blockId: block.id,
            pointerId: event.pointerId,
            captureTarget: event.currentTarget,
            dragActivated: false,
            startClientX: event.clientX,
            startClientY: event.clientY,
            startLeft: block.left ?? box.left,
            startTop: block.top ?? box.top,
        };
    }, []);

    const handleCustomDragMove = useCallback(
        (event) => {
            const session = customDragSessionRef.current;
            if (!session || event.pointerId !== session.pointerId) return;

            const dx = event.clientX - session.startClientX;
            const dy = event.clientY - session.startClientY;
            if (!session.dragActivated) {
                if (Math.abs(dx) <= 2 && Math.abs(dy) <= 2) {
                    return;
                }
                session.dragActivated = true;
                customDragMovedRef.current = true;
                setCustomDragBlockId(session.blockId);
                if (session.captureTarget?.setPointerCapture) {
                    try {
                        session.captureTarget.setPointerCapture(session.pointerId);
                    } catch {
                        // ignore if capture fails
                    }
                }
            }

            const viewport = viewportRef.current;
            if (!viewport) return;

            const newLeft = session.startLeft + dx;
            const newTop = session.startTop + dy;

            setCustomBlocks((previous) =>
                previous.map((block) => {
                    if (block.id !== session.blockId) return block;
                    const displayText = getCustomDisplayText(block) || " ";
                    return repositionCustomBlockViewport(
                        block,
                        newLeft,
                        newTop,
                        viewport,
                        zoom,
                        displayText
                    );
                })
            );
        },
        [getCustomDisplayText, zoom]
    );

    const endCustomBlockDrag = useCallback(
        (event) => {
            const session = customDragSessionRef.current;
            if (!session || event.pointerId !== session.pointerId) return;

            if (
                session.captureTarget?.releasePointerCapture &&
                session.captureTarget.hasPointerCapture?.(session.pointerId)
            ) {
                try {
                    session.captureTarget.releasePointerCapture(session.pointerId);
                } catch {
                    // ignore if capture already released
                }
            }

            const wasDrag = customDragMovedRef.current;
            customDragSessionRef.current = null;
            setCustomDragBlockId("");

            if (wasDrag) {
                setCustomBlocks((previous) => {
                    const block = previous.find((item) => item.id === session.blockId);
                    if (block && editsRef.current[block.id]) {
                        syncCustomEditPosition(block);
                    }
                    return previous;
                });
            } else if (editsRef.current[session.blockId]) {
                const block =
                    customBlocks.find((item) => item.id === session.blockId) ||
                    allBlocksForLookup.find((item) => item.id === session.blockId);
                if (block?.isCustom) {
                    pendingTextCaretRef.current = {
                        clientX: session.startClientX,
                        clientY: session.startClientY,
                        blockId: block.id,
                    };
                    startEditingBlockRef.current(block);
                }
            }

            window.setTimeout(() => {
                customDragMovedRef.current = false;
            }, 0);
        },
        [allBlocksForLookup, customBlocks, syncCustomEditPosition]
    );

    useEffect(() => {
        window.addEventListener("pointermove", handleCustomDragMove);
        window.addEventListener("pointerup", endCustomBlockDrag);
        window.addEventListener("pointercancel", endCustomBlockDrag);
        return () => {
            window.removeEventListener("pointermove", handleCustomDragMove);
            window.removeEventListener("pointerup", endCustomBlockDrag);
            window.removeEventListener("pointercancel", endCustomBlockDrag);
        };
    }, [endCustomBlockDrag, handleCustomDragMove]);

    const commitActiveEdit = () => {
        if (!activeBlock) return;
        const latestBlock =
            allBlocksForLookup.find((item) => item.id === activeBlock.id) || activeBlock;
        const draft = String(activeDraftText ?? "");
        if (!draft.trim()) {
            if (activeBlock.isCustom) {
                setEdits((previous) => {
                    const next = { ...previous };
                    delete next[activeBlock.id];
                    editsRef.current = next;
                    return next;
                });
                if (!activeBlock.erasureZoneId) {
                    removeCustomBlock(activeBlock.id);
                }
                cancelActiveEdit();
                return;
            }
            commitPdfTextErase(latestBlock);
            cancelActiveEdit();
            return;
        }
        if (activeBlock.isCustom) {
            applyCustomBlockText(latestBlock, activeDraftText);
            return;
        }
        applyExistingBlockText(latestBlock, activeDraftText);
    };

    const handleSave = async () => {
        const mergedEdits = { ...edits };
        if (activeBlock) {
            const latestBlock =
                allBlocksForLookup.find((item) => item.id === activeBlock.id) || activeBlock;
            const entry = buildEditEntry(
                latestBlock,
                activeDraftText,
                stylePatchForBlock(latestBlock),
                mergedEdits,
                allBlocksForLookup
            );
            if (entry) mergedEdits[activeBlock.id] = entry;
            else delete mergedEdits[activeBlock.id];
        }
        const zoneReplacements = erasureZones.map((zone) => ({
            page: zone.page,
            originalText: zone.originalText,
            newText: "",
            isInsert: false,
            x: zone.x,
            y: zone.y,
            width: zone.width,
            height: zone.height,
            fontSize: zone.fontSize,
            pdfFontKey: zone.pdfFontKey,
            cssFontFamily: zone.cssFontFamily,
            cssFontWeight: zone.cssFontWeight,
            cssFontStyle: zone.cssFontStyle,
            textColorR: zone.textColorR,
            textColorG: zone.textColorG,
            textColorB: zone.textColorB,
            fillColorR: zone.fillColorR,
            fillColorG: zone.fillColorG,
            fillColorB: zone.fillColorB,
            styleChanged: false,
        }));
        const replacements = [...zoneReplacements, ...Object.values(mergedEdits)];
        if (!replacements.length) {
            toast.info("No changes to save yet");
            return;
        }

        if (!workingBytes) {
            toast.error("PDF data is not loaded");
            return;
        }

        setSaving(true);
        try {
            const resolvedFileName = String(fileName || "").trim() || "document.pdf";
            const nextBytes = await buildSavedPdfBytes(workingBytes, replacements, {
                outputFileName: resolvedFileName,
                flatten: flattenOnSave,
            });
            const file = pdfBytesToFile(nextBytes, resolvedFileName);
            await Promise.resolve(onSave?.(file, nextBytes));
            const normalizedNext = new Uint8Array(nextBytes);
            setWorkingBytes(normalizedNext);
            workingBytesRef.current = normalizedNext;
            erasureReplacementsRef.current = zoneReplacements;
            setEdits({});
            setErasureZones([]);
            setCustomBlocks([]);
            cancelActiveEdit();
            invalidateTextExtractCache();
            if (!flattenOnSave) {
                await loadDocument({
                    resetSession: false,
                    bytesOverride: normalizedNext,
                    silent: true,
                });
            } else {
                cancelActivePageRender();
            }
        } catch (error) {
            const message =
                error?.message ||
                "Unable to save PDF. Try shorter text or simpler characters.";
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    const mergedChangeList = useMemo(() => {
        const list = editEntriesFromEdits(edits, allBlocksForLookup);
        if (activeBlock) {
            const entry = buildEditEntry(
                activeBlock,
                activeDraftText,
                stylePatchForBlock(activeBlock),
                edits,
                allBlocksForLookup
            );
            const existingIndex = list.findIndex((item) => item.blockId === activeBlock.id);
            if (entry) {
                const row = {
                    blockId: activeBlock.id,
                    page: activeBlock.page,
                    isLive: true,
                    ...entry,
                };
                if (existingIndex >= 0) {
                    const next = [...list];
                    next[existingIndex] = row;
                    return next;
                }
                return [row, ...list];
            }
            if (existingIndex >= 0) {
                return list.filter((item) => item.blockId !== activeBlock.id);
            }
        }
        return list;
    }, [
        activeBlock,
        activeDraftText,
        activeSelectStyle,
        buildEditEntry,
        edits,
        allBlocksForLookup,
        stylePatchForBlock,
    ]);

    if (!pdfDoc && loading) {
        return <Loaders />;
    }

    if (!pdfDoc) {
        return (
            <div className="pdf-inline-text-editor-empty text-danger">
                Unable to load this PDF for editing.
            </div>
        );
    }

    const totalChangeCount = mergedChangeList.length + erasureZones.length;
    const activeCustomBlock = activeBlock?.isCustom ? activeBlock : null;
    const isEditingExistingText = Boolean(
        activeBlockId && activeBlock && !activeBlock.isCustom
    );
    /** Hit targets only — canvas + mask show text (prevents stacked/ghost glyphs). */
    const suppressTextLayerGlyphs = true;

    const stageOverlayToolClass = addTextMode
        ? "t-text"
        : activeBlockId
          ? "t-edittext"
          : "t-select";

    const goToPage = (nextPage) => {
        cancelActiveEdit();
        setPageNumber(nextPage);
    };

    const showToolbarTextStyle = Boolean(
        isEditingExistingText || activeCustomBlock || addTextMode
    );
    const toolbarStyleFontSize = isEditingExistingText
        ? Math.round(activeSelectStyle?.fontSize ?? activeBlock?.fontSize ?? 12)
        : activeCustomBlock
          ? Math.round(activeCustomBlock.fontSize || 12)
          : Math.round(newTextToolStyle.fontSize || DEFAULT_INSERT_FONT_SIZE);
    const toolbarStyleTextColor = isEditingExistingText
        ? activeSelectStyle?.textColor ??
          activeBlock?.textColor ?? { r: 0, g: 0, b: 0 }
        : activeCustomBlock
          ? activeCustomBlock.textColor || { r: 0, g: 0, b: 0 }
          : newTextToolStyle.textColor || { r: 0, g: 0, b: 0 };
    const toolbarFontKey = isEditingExistingText
        ? activeSelectStyle?.pdfFontKey ?? activeBlock?.pdfFontKey ?? "Helvetica"
        : activeCustomBlock
          ? activeCustomBlock.pdfFontKey || "Helvetica"
          : newTextToolStyle.pdfFontKey || "Helvetica";

    const handleToolbarFontSizeChange = (event) => {
        const size = Math.min(
            MAX_CUSTOM_FONT_SIZE,
            Math.max(
                MIN_CUSTOM_FONT_SIZE,
                Number(event.target.value) ||
                    (isEditingExistingText
                        ? Number(activeBlock?.fontSize)
                        : DEFAULT_INSERT_FONT_SIZE) ||
                    DEFAULT_INSERT_FONT_SIZE
            )
        );
        if (isEditingExistingText) {
            updateActiveSelectStyle({ fontSize: size });
            refocusActiveTextInput();
            return;
        }
        if (activeCustomBlock) {
            updateActiveCustomBlock({ fontSize: size, pdfHeight: size * 1.15 });
            setNewTextToolStyle((previous) => ({ ...previous, fontSize: size }));
            refocusActiveTextInput();
            return;
        }
        setNewTextToolStyle((previous) => ({ ...previous, fontSize: size }));
    };

    const handleToolbarTextColorChange = (event) => {
        const textColor = hexToNormalizedRgb(event.target.value);
        if (isEditingExistingText) {
            updateActiveSelectStyle({ textColor });
            refocusActiveTextInput();
            return;
        }
        if (activeCustomBlock) {
            updateActiveCustomBlock({ textColor });
            setNewTextToolStyle((previous) => ({ ...previous, textColor }));
            refocusActiveTextInput();
            return;
        }
        setNewTextToolStyle((previous) => ({ ...previous, textColor }));
    };

    const handleToolbarFontFamilyChange = (event) => {
        const meta = fontMetaFromPdfKey(event.target.value);
        if (isEditingExistingText) {
            updateActiveSelectStyle(meta);
            refocusActiveTextInput();
            return;
        }
        if (activeCustomBlock) {
            updateActiveCustomBlock(meta);
            setNewTextToolStyle((previous) => ({ ...previous, ...meta }));
            refocusActiveTextInput();
            return;
        }
        setNewTextToolStyle((previous) => ({ ...previous, ...meta }));
    };

    const showApplyToolbar = Boolean(activeBlockId || addTextMode);

    return (
        <div className="pdf-inline-text-editor pdf-pit-stop-editor pdf-pit-stop-light">
            <div className="pdf-pit-workspace">
                <aside className="pdf-pit-preview" aria-label="Page previews">
                    <div className="pdf-pit-preview-head">
                        <span className="pdf-pit-preview-title">Preview</span>
                        <span className="pdf-pit-preview-count">{pageCount} pages</span>
                    </div>
                    <div className="pdf-pit-thumbs">
                        {thumbsLoading && !pageThumbnails.length
                            ? Array.from({ length: Math.min(pageCount, 6) }, (_, index) => (
                                  <div
                                      key={`thumb-skeleton-${index}`}
                                      className="pdf-pit-thumb is-skeleton"
                                      aria-hidden="true"
                                  />
                              ))
                            : null}
                        {(pageThumbnails.length
                            ? pageThumbnails
                            : Array.from({ length: pageCount }, (_, index) => ({
                                  page: index + 1,
                                  url: "",
                              }))
                        ).map((thumb) => (
                            <button
                                key={`page-thumb-${thumb.page}`}
                                type="button"
                                className={classNames("pdf-pit-thumb", {
                                    "is-active": thumb.page === pageNumber,
                                })}
                                onClick={() => goToPage(thumb.page)}
                                title={`Page ${thumb.page}`}
                            >
                                {thumb.url ? (
                                    <img src={thumb.url} alt="" className="pdf-pit-thumb-img" />
                                ) : (
                                    <span className="pdf-pit-thumb-fallback">{thumb.page}</span>
                                )}
                                <span className="pdf-pit-thumb-label">{thumb.page}</span>
                            </button>
                        ))}
                    </div>
                </aside>

                <div className="pdf-pit-main">
                    <div className="pdf-inline-text-editor-toolbar pdf-pit-toolbar">
                        <div className="pdf-inline-text-editor-toolbar-left pdf-pit-toolbar-left">
                            <div className="pdf-pit-tool-group">
                                <button
                                    type="button"
                                    className={classNames("pdf-pit-tool-item", {
                                        "is-active": !addTextMode,
                                    })}
                                    onClick={() => {
                                        cancelActiveEdit();
                                        setAddTextMode(false);
                                    }}
                                    title="Select and edit text"
                                >
                                    <i className="mdi mdi-cursor-default-outline" aria-hidden="true" />
                                    <span>Select</span>
                                </button>
                                <button
                                    type="button"
                                    className={classNames("pdf-pit-tool-item", {
                                        "is-active": addTextMode,
                                    })}
                                        onClick={() => {
                                        cancelActiveEdit();
                                        setAddTextMode((previous) => {
                                            const next = !previous;
                                            if (next) {
                                                setNewTextToolStyle((style) => ({
                                                    ...style,
                                                    fontSize: DEFAULT_INSERT_FONT_SIZE,
                                                }));
                                                // toast.info(
                                                //     "Click on the PDF where you want the new text"
                                                // );
                                            }
                                            return next;
                                        });
                                    }}
                                    title="Add new text"
                                >
                                    <i className="mdi mdi-format-textbox" aria-hidden="true" />
                                    <span>Add New Text</span>
                                </button>
                            </div>
                            {showApplyToolbar ? (
                                <button
                                    type="button"
                                    className="pdf-pit-pill pdf-pit-pill-success"
                                    onMouseDown={(event) => event.preventDefault()}
                                    disabled={addTextMode && !activeBlockId}
                                    title={
                                        addTextMode && !activeBlockId
                                            ? "Click the page to place text first"
                                            : "Apply to page"
                                    }
                                    onClick={commitActiveEdit}
                                >
                                    Apply
                                </button>
                            ) : null}
                            <div className="pdf-pit-meta-chips">
                                {activeBlockId ? (
                                    <span className="pdf-pit-chip pdf-pit-chip-accent">Editing</span>
                                ) : null}
                                {totalChangeCount ? (
                                    <span className="pdf-pit-chip">
                                        {totalChangeCount} change
                                        {totalChangeCount === 1 ? "" : "s"}
                                    </span>
                                ) : null}
                            </div>
                            {showToolbarTextStyle ? (
                                <div
                                    className="pdf-pit-toolbar-style-tools pdf-inline-text-editor-style-panel"
                                    role="group"
                                    aria-label="Text size and color"
                                >
                                    <label className="pdf-pit-toolbar-style-field pdf-pit-toolbar-style-field-font">
                                        <span className="pdf-pit-toolbar-style-label">Font</span>
                                        <select
                                            className="form-select form-select-sm pdf-pit-toolbar-style-select"
                                            value={toolbarFontKey}
                                            onChange={handleToolbarFontFamilyChange}
                                        >
                                            {NEW_TEXT_FONT_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="pdf-pit-toolbar-style-field">
                                        <span className="pdf-pit-toolbar-style-label">Size (pt)</span>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm pdf-pit-toolbar-style-input"
                                            min={MIN_CUSTOM_FONT_SIZE}
                                            max={MAX_CUSTOM_FONT_SIZE}
                                            value={toolbarStyleFontSize}
                                            onChange={handleToolbarFontSizeChange}
                                        />
                                    </label>
                                    <label className="pdf-pit-toolbar-style-field pdf-pit-toolbar-style-field-color">
                                        <span className="pdf-pit-toolbar-style-label">
                                            Text color
                                        </span>
                                        <input
                                            type="color"
                                            className="form-control form-control-sm form-control-color pdf-pit-toolbar-style-color"
                                            value={rgbToHex(toolbarStyleTextColor)}
                                            onChange={handleToolbarTextColorChange}
                                        />
                                    </label>
                                </div>
                            ) : null}
                        </div>
                        <div className="pdf-inline-text-editor-toolbar-right pdf-pit-toolbar-tools">
                            <button
                                type="button"
                                className="pdf-pit-icon-btn"
                                disabled={zoomIndex <= 0}
                                onClick={() => setZoomIndex((index) => Math.max(0, index - 1))}
                                title="Zoom out"
                            >
                                <i className="mdi mdi-magnify-minus-outline" aria-hidden="true" />
                            </button>
                            <span className="pdf-pit-zoom-label">{Math.round(zoom * 100)}%</span>
                            <button
                                type="button"
                                className="pdf-pit-icon-btn"
                                disabled={zoomIndex >= ZOOM_LEVELS.length - 1}
                                onClick={() =>
                                    setZoomIndex((index) =>
                                        Math.min(ZOOM_LEVELS.length - 1, index + 1)
                                    )
                                }
                                title="Zoom in"
                            >
                                <i className="mdi mdi-magnify-plus-outline" aria-hidden="true" />
                            </button>
                            <div className="pdf-pit-tool-divider" aria-hidden="true" />
                            <button
                                type="button"
                                className="pdf-pit-icon-btn"
                                disabled={pageNumber <= 1}
                                onClick={() => goToPage(Math.max(1, pageNumber - 1))}
                                title="Previous page"
                            >
                                <i className="mdi mdi-chevron-left" aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className="pdf-pit-icon-btn"
                                disabled={pageNumber >= pageCount}
                                onClick={() => goToPage(Math.min(pageCount, pageNumber + 1))}
                                title="Next page"
                            >
                                <i className="mdi mdi-chevron-right" aria-hidden="true" />
                            </button>
                        </div>
                    </div>

                    <p className="pdf-inline-text-editor-hint">
                        Click the end of any existing text to edit it. To add new text, choose{" "}
                        <strong>Add New Text</strong>, click the page, then drag the text to position it.
                        Click <strong>Apply</strong> before you
                        change pages, or edits on the current page may be lost. When you are done,
                        use <strong>Update Button</strong>.
                    </p>

                    <div className="pdf-pit-canvas-shell">
                        <div
                            ref={stageRef}
                            className={classNames(
                                "pdf-inline-text-editor-stage pdf-pit-stage-overlay",
                                stageOverlayToolClass,
                                { "is-add-text-mode": addTextMode }
                            )}
                        >
                        {loading ? (
                            <div
                                className="pdf-inline-text-editor-loading-overlay"
                                aria-busy="true"
                            >
                                <Loaders />
                            </div>
                        ) : null}
                        <canvas ref={canvasRef} className="pdf-inline-text-editor-canvas" />
                        <canvas
                            ref={maskCanvasRef}
                            className="pdf-inline-text-editor-mask-canvas"
                            aria-hidden="true"
                        />
                        <div
                            ref={textLayerRef}
                            className={classNames("pdf-inline-text-editor-text-layer", {
                                "is-suppress-span-glyphs": suppressTextLayerGlyphs,
                            })}
                        >
                            {textExtractReady &&
                            textBlocks.length === 0 &&
                            !customBlocks.some((b) => b.page === pageNumber) ? (
                                <div className="pdf-inline-text-editor-no-text">
                                    This page has no clickable text layer (often scanned or
                                    flattened). Use <strong>Text</strong> in the toolbar and click
                                    the page to add or replace text.
                                </div>
                            ) : null}
                            {visibleBlocks.map((block) => {
                                const isActive = activeBlockId === block.id;
                                const isApplied = Boolean(edits[block.id]);
                                const hasChange = blockHasVisibleChange(
                                    block,
                                    edits,
                                    activeBlockId,
                                    activeDraftText,
                                    activeSelectStyle
                                );
                                const typography = blockTypography(
                                    block,
                                    isApplied ? edits[block.id] : null
                                );

                                if (block.isCustom && isActive) {
                                    const liveCustomStyle = styleEditShapeFromBlock(block);
                                    const customTypography = blockTypography(
                                        block,
                                        liveCustomStyle
                                    );
                                    const customBox = measureViewportBoxForContent(
                                        block,
                                        activeDraftText || " ",
                                        customTypography,
                                        zoom
                                    );
                                    const inlineEditColor = colorToCss(
                                        resolveTextColor(block, liveCustomStyle)
                                    );
                                    const lineHeightPx =
                                        customBox.fontSize * LINE_HEIGHT_FACTOR;
                                    const editorLeft =
                                        block.left ??
                                        block.placementViewportX ??
                                        customBox.left;
                                    const editorTop =
                                        block.top ??
                                        block.placementViewportY ??
                                        customBox.top;
                                    const editorWidth = Math.max(customBox.width, 24);
                                    const editorMinHeight = Math.max(
                                        customBox.height,
                                        lineHeightPx
                                    );
                                    return (
                                        <textarea
                                            key={block.id}
                                            ref={activeInputRef}
                                            autoFocus
                                            className="pdf-inline-text-editor-input is-new-text-plain is-inline-pdf-edit is-new-text-typing"
                                            placeholder=""
                                            rows={1}
                                            style={{
                                                left: `${editorLeft}px`,
                                                top: `${editorTop}px`,
                                                minWidth: `${editorWidth}px`,
                                                width: `${editorWidth}px`,
                                                minHeight: `${editorMinHeight}px`,
                                                fontSize: `${customBox.fontSize}px`,
                                                lineHeight: LINE_HEIGHT_FACTOR,
                                                fontFamily: customTypography.cssFontFamily,
                                                fontWeight: customTypography.cssFontWeight,
                                                fontStyle: customTypography.cssFontStyle,
                                                "--pdf-inline-edit-color": inlineEditColor,
                                                "--pdf-inline-edit-font-family":
                                                    customTypography.cssFontFamily,
                                                "--pdf-inline-edit-font-size": `${customBox.fontSize}px`,
                                                "--pdf-inline-edit-font-weight":
                                                    customTypography.cssFontWeight,
                                                "--pdf-inline-edit-font-style":
                                                    customTypography.cssFontStyle,
                                                color: inlineEditColor,
                                                WebkitTextFillColor: inlineEditColor,
                                                caretColor: inlineEditColor,
                                                background: "transparent",
                                                zIndex: 10,
                                            }}
                                            value={activeDraftText}
                                            onMouseDown={(event) => {
                                                event.stopPropagation();
                                            }}
                                            onChange={(event) => {
                                                handleCustomDraftChange(event.target.value);
                                            }}
                                            onBlur={(event) =>
                                                handleInlineTextBlur(
                                                    block,
                                                    event.target.value,
                                                    event
                                                )
                                            }
                                            onKeyDown={(event) => {
                                                if (
                                                    event.key === "Enter" &&
                                                    (event.ctrlKey || event.metaKey)
                                                ) {
                                                    event.preventDefault();
                                                    commitActiveEdit();
                                                    return;
                                                }
                                                if (event.key === "Escape") {
                                                    event.preventDefault();
                                                    cancelActiveEdit();
                                                }
                                            }}
                                        />
                                    );
                                }

                                if (
                                    block.isCustom &&
                                    !isActive &&
                                    (isApplied || hasChange) &&
                                    String(edits[block.id]?.newText ?? "").trim()
                                ) {
                                    const customText = edits[block.id]?.newText || "";
                                    const customTypography = blockTypography(
                                        block,
                                        isApplied ? edits[block.id] : null
                                    );
                                    const customBox = measureViewportBoxForContent(
                                        block,
                                        customText,
                                        customTypography,
                                        zoom
                                    );
                                    return (
                                        <div
                                            key={block.id}
                                            role="button"
                                            tabIndex={0}
                                            className={classNames(
                                                "pdf-inline-text-editor-custom-drag",
                                                {
                                                    "is-dragging":
                                                        customDragBlockId === block.id,
                                                }
                                            )}
                                            style={{
                                                left: `${block.left ?? customBox.left}px`,
                                                top: `${block.top ?? customBox.top}px`,
                                                width: `${Math.max(customBox.width, 48)}px`,
                                                height: `${Math.max(customBox.height, 24)}px`,
                                                cursor: "grab",
                                            }}
                                            title="Drag to move · Click to edit"
                                            onPointerDown={(event) => {
                                                if (event.button !== 0) return;
                                                beginCustomBlockDrag(block, event, customBox);
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    startEditingBlock(block);
                                                }
                                            }}
                                        />
                                    );
                                }

                                if (isActive && !block.isCustom) {
                                    const activeStyleEdit = activeSelectStyle
                                        ? {
                                              fontSize: activeSelectStyle.fontSize,
                                              textColorR: activeSelectStyle.textColor?.r ?? 0,
                                              textColorG: activeSelectStyle.textColor?.g ?? 0,
                                              textColorB: activeSelectStyle.textColor?.b ?? 0,
                                              pdfFontKey: activeSelectStyle.pdfFontKey,
                                              cssFontFamily: activeSelectStyle.cssFontFamily,
                                              cssFontWeight: activeSelectStyle.cssFontWeight,
                                              cssFontStyle: activeSelectStyle.cssFontStyle,
                                          }
                                        : null;
                                    const activeTypography = blockTypography(
                                        block,
                                        activeStyleEdit
                                    );
                                    const draftLine = String(activeDraftText ?? "").replace(
                                        /\r?\n/g,
                                        " "
                                    );
                                    const activeBox = measureViewportBoxForContent(
                                        block,
                                        draftLine || " ",
                                        activeTypography,
                                        zoom
                                    );
                                    const inlineEditColor = colorToCss(
                                        resolveTextColor(block, activeStyleEdit)
                                    );
                                    const lineHeightPx =
                                        activeBox.fontSize * LINE_HEIGHT_FACTOR;
                                    return (
                                        <input
                                            key={block.id}
                                            ref={activeInputRef}
                                            type="text"
                                            autoFocus
                                            className="pdf-inline-text-editor-input is-new-text-plain is-inline-pdf-edit is-inline-pdf-textfield"
                                            placeholder=""
                                            style={{
                                                left: `${activeBox.left}px`,
                                                top: `${activeBox.top}px`,
                                                minWidth: `${Math.max(block.width, 12)}px`,
                                                width: `${activeBox.width}px`,
                                                height: `${lineHeightPx}px`,
                                                fontSize: `${activeBox.fontSize}px`,
                                                lineHeight: `${lineHeightPx}px`,
                                                fontFamily: activeTypography.cssFontFamily,
                                                fontWeight: activeTypography.cssFontWeight,
                                                fontStyle: activeTypography.cssFontStyle,
                                                "--pdf-inline-edit-color": inlineEditColor,
                                                "--pdf-inline-edit-font-family":
                                                    activeTypography.cssFontFamily,
                                                "--pdf-inline-edit-font-size": `${activeBox.fontSize}px`,
                                                "--pdf-inline-edit-font-weight":
                                                    activeTypography.cssFontWeight,
                                                "--pdf-inline-edit-font-style":
                                                    activeTypography.cssFontStyle,
                                                color: inlineEditColor,
                                                WebkitTextFillColor: inlineEditColor,
                                                caretColor: inlineEditColor,
                                                background: PDF_EDIT_SURFACE,
                                                zIndex: 10,
                                            }}
                                            value={draftLine}
                                            onMouseDown={(event) => {
                                                event.stopPropagation();
                                            }}
                                            onChange={(event) => {
                                                setActiveDraftText(event.target.value);
                                            }}
                                            onBlur={(event) =>
                                                handleInlineTextBlur(
                                                    block,
                                                    event.target.value,
                                                    event
                                                )
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    commitActiveEdit();
                                                    return;
                                                }
                                                if (event.key === "Escape") {
                                                    event.preventDefault();
                                                    cancelActiveEdit();
                                                    return;
                                                }
                                            }}
                                        />
                                    );
                                }

                                if (isApplied || hasChange) {
                                    const previewText = resolveBlockDisplayText(block, edits);
                                    if (
                                        isErasedEdit(edits[block.id]) ||
                                        !String(previewText ?? "").trim()
                                    ) {
                                        return null;
                                    }
                                    const box = measureViewportBoxForContent(
                                        block,
                                        previewText,
                                        typography,
                                        zoom
                                    );
                                    return (
                                        <span
                                            key={block.id}
                                            role="button"
                                            tabIndex={0}
                                            className="pdf-inline-text-editor-span is-applied is-canvas-preview"
                                            style={{
                                                left: `${box.left}px`,
                                                top: `${box.top}px`,
                                                minWidth: `${box.width}px`,
                                                minHeight: `${box.height}px`,
                                            }}
                                            title="Click to edit again"
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                startEditingBlock(block, event);
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    startEditingBlock(block);
                                                }
                                            }}
                                        >
                                            <span className="visually-hidden">{previewText}</span>
                                        </span>
                                    );
                                }

                                return (
                                    isErasedEdit(edits[block.id]) ||
                                    !String(resolveBlockDisplayText(block, edits)).trim()
                                ) ? null : (
                                    <span
                                        key={block.id}
                                        role="button"
                                        tabIndex={0}
                                        className="pdf-inline-text-editor-span"
                                        style={{
                                            left: `${block.left}px`,
                                            top: `${block.top}px`,
                                            width: `${Math.max(block.width, 12)}px`,
                                            height: `${Math.max(
                                                block.height,
                                                block.viewportFontSize * LINE_HEIGHT_FACTOR
                                            )}px`,
                                            fontSize: `${block.viewportFontSize}px`,
                                            lineHeight: LINE_HEIGHT_FACTOR,
                                            fontFamily: typography.cssFontFamily,
                                            fontWeight: typography.cssFontWeight,
                                            fontStyle: typography.cssFontStyle,
                                            zIndex: isActive ? 30 : 1,
                                        }}
                                        title="Click to edit this text"
                                        onMouseDown={(event) => {
                                            event.preventDefault();
                                            startEditingBlock(block, event);
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                startEditingBlock(block);
                                            }
                                        }}
                                    >
                                        <span className="visually-hidden">
                                            {resolveBlockDisplayText(block, edits)}
                                        </span>
                                    </span>
                                );
                            })}
                        </div>
                        {addTextMode && pageLayout.width > 0 ? (
                            <div
                                className="pdf-inline-text-editor-placement-layer t-text"
                                role="presentation"
                                style={{
                                    width: `${pageLayout.width}px`,
                                    height: `${pageLayout.height}px`,
                                }}
                                onPointerDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handlePlaceNewText(event);
                                }}
                            />
                        ) : null}
                        <div className="pdf-pit-status-bar" aria-live="polite">
                            <span>
                                Page {pageNumber} of {pageCount}
                            </span>
                            <span className="pdf-pit-status-dot" aria-hidden="true" />
                            <span>{Math.round(zoom * 100)}% zoom</span>
                            {totalChangeCount ? (
                                <>
                                    <span className="pdf-pit-status-dot" aria-hidden="true" />
                                    <span>
                                        {totalChangeCount} pending change
                                        {totalChangeCount === 1 ? "" : "s"}
                                    </span>
                                </>
                            ) : null}
                        </div>
                    </div>
                    </div>

                    <div className="pdf-inline-text-editor-actions pdf-pit-footer-actions">
                <button
                    type="button"
                    className="pdf-pit-pill pdf-pit-pill-ghost"
                    onClick={onCancel}
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="pdf-pit-pill pdf-pit-pill-primary"
                    onClick={handleSave}
                    disabled={
                        saving ||
                        (totalChangeCount === 0 && !activeBlockId)
                    }
                >
                    {saving
                        ? String(saveButtonLabel).toLowerCase().includes("update")
                            ? "Updating…"
                            : "Saving…"
                        : saveButtonLabel}
                </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

function editEntriesFromEdits(edits, textBlocks) {
    return Object.entries(edits).map(([blockId, change]) => {
        const block = textBlocks.find((item) => item.id === blockId);
        return {
            blockId,
            page: change.page ?? block?.page ?? 1,
            isLive: false,
            ...change,
        };
    });
}

PdfInlineTextEditor.propTypes = {
    sourceBytes: PropTypes.oneOfType([
        PropTypes.instanceOf(ArrayBuffer),
        PropTypes.instanceOf(Uint8Array),
    ]).isRequired,
    fileName: PropTypes.string,
    saveButtonLabel: PropTypes.string,
    flattenOnSave: PropTypes.bool,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
};

export default PdfInlineTextEditor;
